import { useState, useEffect } from "react";
import HolidayTab from "./HolidayTab";
import WeekendTab from "./WeekendTab";
import WorkingHoursTab from "./WeekendHoursTab";
import AttendancePolicyForm from "./AttendancePolicyForm";
import { useNavigate } from "react-router";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";

export default function AttendencePolicies({ onBack }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchBranchAttendenceSummary();
    }
  }, [activeTab]);

  const fetchBranchAttendenceSummary = async () => {
    setLoading(true);
    try {
      const response = await apiUrl.get("/hr/policies/branchAttendanceSummary");
      if (response?.status) {
        setBranches(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100">
      {/* Header */}
      <header className="border-bottom bg-light">
        <div className="container px-4 py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <a
                className="btn btn-link p-0 me-3"
                onClick={() => navigate("/manage-policies")}
                style={{ textDecoration: "none" }}
              >
                <FaIcons.FaArrowAltCircleLeft size={22} className="me-1" />
                <span className="fw-bold">Back</span>
              </a>
              <div>
                <h1 className="h3 fw-bold">Attendance Policies</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container px-4 py-3">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-4" role="tablist">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                activeTab === "working-hours" ? "active" : ""
              }`}
              onClick={() => setActiveTab("working-hours")}
            >
              Work Schedule
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "weekends" ? "active" : ""}`}
              onClick={() => setActiveTab("weekends")}
            >
              Weekends
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "holidays" ? "active" : ""}`}
              onClick={() => setActiveTab("holidays")}
            >
              Holidays
            </button>
          </li>
        </ul>

        <div className="tab-content">
          {/* Overview Tab */}
          <div
            className={`tab-pane ${activeTab === "overview" ? "active" : ""}`}
          >
            <div className="card shadow-sm border mb-2">
              <div className="card-body">
                <div className="d-flex gap-2 flex-wrap">
                  {/* Working Hours */}
                  <div className="col-12 col-md-3">
                    <button
                      className="btn btn-outline-primary w-100 h-100 text-start p-3 d-flex flex-column align-items-start gap-2"
                      onClick={() => setActiveTab("working-hours")}
                    >
                      <FaIcons.FaClock
                        className="text-primary mb-1"
                        size={20}
                      />
                      <div>
                        <p className="fw-semibold mb-1">Update Work Schedule</p>
                        <small>Modify daily schedules</small>
                      </div>
                    </button>
                  </div>

                  {/* Weekends */}
                  <div className="col-12 col-md-3">
                    <button
                      className="btn btn-outline-secondary w-100 h-100 text-start p-3 d-flex flex-column align-items-start gap-2"
                      onClick={() => setActiveTab("weekends")}
                    >
                      <FaIcons.FaWeibo
                        className="text-secondary mb-1"
                        size={20}
                      />
                      <div>
                        <p className="fw-semibold mb-1">Manage Weekends</p>
                        <small>Set weekend days</small>
                      </div>
                    </button>
                  </div>

                  {/* Holidays */}
                  <div className="col-12 col-md-3">
                    <button
                      className="btn btn-outline-success w-100 h-100 text-start p-3 d-flex flex-column align-items-start gap-2"
                      onClick={() => setActiveTab("holidays")}
                    >
                      <FaIcons.FaCalendarAlt
                        className="text-success mb-1"
                        size={20}
                      />
                      <div>
                        <p className="fw-semibold mb-1">Manage Holidays</p>
                        <small>Add or edit holidays</small>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Branch Cards */}
            <div className="mb-4">
              <div className="row g-3">
                {loading
                  ? [...Array(4)].map((_, idx) => (
                      <div key={idx} className="col-12 col-sm-6 col-lg-3">
                        <div className="card shadow-sm p-3">
                          <div className="placeholder-glow">
                            <span className="placeholder col-6 mb-2"></span>
                            <span className="placeholder col-8 mb-2"></span>
                            <span className="placeholder col-4 mb-2"></span>
                            <span className="placeholder col-7"></span>
                          </div>
                        </div>
                      </div>
                    ))
                  : branches.map((branch, index) => (
                      <div
                        key={index}
                        className="col-12 col-sm-6 col-lg-3 gap-2"
                      >
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-white border-0 pb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="p-2 rounded bg-primary text-white">
                                <FaIcons.FaMap size={16} />
                              </div>
                              <h6 className="mb-0">{branch.name}</h6>
                            </div>
                          </div>

                          <div className="card-body">
                            {/* Working Hours */}
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <FaIcons.FaClock
                                size={16}
                                className="text-primary"
                              />
                              <div>
                                <p className="mb-0 small fw-medium">
                                  Working Hours
                                </p>
                                <small className="text-muted">
                                  {branch.workingHours}
                                </small>
                              </div>
                            </div>

                            {/* Weekend Days */}
                            <div className="mb-3">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaIcons.FaCalendar
                                  size={16}
                                  className="text-purple"
                                />
                                <p className="mb-0 small fw-medium">
                                  Weekend Days
                                </p>
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {branch.weekends.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className="badge bg-light text-dark border"
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Key Metrics */}
                            <div className="d-flex justify-content-between border-top pt-2 text-center">
                              <div>
                                <p className="h6 mb-0">{branch.holidays}</p>
                                <small className="text-muted">Holidays</small>
                              </div>
                              <div>
                                <p className="h6 mb-0">{branch.workingDays}</p>
                                <small className="text-muted">Work Days</small>
                              </div>
                              <div>
                                <p className="h6 mb-0">
                                  {branch.lateThreshold}
                                </p>
                                <small className="text-muted">Late After</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            </div>
          </div>

          {/* Working Hours Tab */}
          <div
            className={`tab-pane ${
              activeTab === "working-hours" ? "active" : ""
            }`}
          >
            {activeTab === "working-hours" && <WorkingHoursTab />}
          </div>

          {/* Weekends Tab */}
          <div
            className={`tab-pane ${activeTab === "weekends" ? "active" : ""}`}
          >
            {activeTab === "weekends" && <WeekendTab />}
          </div>

          {/* Holidays Tab */}
          <div
            className={`tab-pane ${activeTab === "holidays" ? "active" : ""}`}
          >
            {activeTab === "holidays" && <HolidayTab />}
          </div>
        </div>
      </div>

      {/* Create Policy Modal */}
      {showCreateForm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <AttendancePolicyForm onClose={() => setShowCreateForm(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
